/**
 * browseFilters — THE filter model for the template browse surface (WS-K3, owner item O-3).
 *
 * The owner's report is three sentences: replace the index table with filters — case type,
 * brand, model — as multi-select check marks; full-width detail blocks; make it intuitive. This
 * module is the first of those, and it is the part that can be WRONG in a way a screenshot
 * never shows: what a count means, what happens to a row with no value, and which half of a
 * selection the server can actually express.
 *
 * It is a STAGE on WS-B's pipeline, never a second pipeline:
 *
 *     templates                                          ← useTemplateList, untouched
 *       → visibleRows({ templates, query, facet, … })    ← rowSet.ts, untouched
 *       → applyFacets(visible, selection, ctx)           ← here
 *       = shown
 *
 * `shown` is what the blocks render, what the list renders, what the bulk bar selects out of,
 * what Force sync sweeps and what the count line reports. ONE array, exactly as F-31 demanded
 * and WS-B delivered.
 *
 * Pure and DOM-free by construction: no React import, no `localStorage`, no `window`. The rules
 * below are therefore unit-tested rather than argued about, and the 2 000-row latency number
 * (G-40) is meaningful because nothing here is tangled in React state.
 */
import type { PhoneModelOption, ProductTemplate } from "@genz/platform";
import { devicePresence, presenceBadge, type DevicePresence } from "./devicePresence.js";
import {
  brandOf, categoryLabel, headStatusOf, modelLabel, statusLabel, STATUS_FACETS, facetMatches,
  type StatusFacet,
} from "./templateLabels.js";
import { EMPTY_FILTERS, type Filters } from "./useTemplateList.js";

/* ───────────────────────────── the shape ───────────────────────────── */

export type FacetKey = "productType" | "brand" | "model" | "status" | "library" | "device";

/**
 * The rail's group order, and therefore the chip row's order too.
 *
 * Declared once so the two can never disagree: a chip row that reorders itself by click order
 * makes the summary above the results read differently from the rail beside them, and the
 * operator has to re-find their own filter every time they add one.
 */
export const FACET_ORDER: readonly FacetKey[] = [
  "productType", "brand", "model", "status", "library", "device",
];

export const FACET_LEGEND: Readonly<Record<FacetKey, string>> = {
  // D-3: "Product type" is the ONE name for this concept. The owner's word is "case type";
  // adopting it here would re-open a decision the product already took, and the meaning is
  // served either way.
  productType: "Product type",
  brand: "Brand",
  model: "Model",
  status: "Status",
  library: "Source",
  device: "On this device",
};

export type FacetSelection = Readonly<Record<FacetKey, readonly string[]>>;

export const EMPTY_SELECTION: FacetSelection = Object.freeze({
  productType: Object.freeze([]) as readonly string[],
  brand: Object.freeze([]) as readonly string[],
  model: Object.freeze([]) as readonly string[],
  status: Object.freeze([]) as readonly string[],
  library: Object.freeze([]) as readonly string[],
  device: Object.freeze([]) as readonly string[],
});

export interface FacetOption {
  value: string;
  label: string;
  count: number;
  /** Checked right now. */
  selected: boolean;
  /** Zero rows AND not selected → the option is rendered greyed rather than removed (M7). A
   *  SELECTED zero option is never disabled: the operator has to be able to undo it. */
  disabled: boolean;
}

export interface FacetGroup {
  key: FacetKey;
  legend: string;
  options: FacetOption[];
  /** How many options the rail shows before `Show all (N)` (M5). Every option is returned —
   *  capping is a RENDER concern, and putting it here would make the count line lie. */
  visibleCap: number;
  /** Above this many options the rail adds a filter-within-the-group box (M1's `Find brand`).
   *  A constant here rather than a magic number in the TSX, because the owner question about
   *  flat-vs-nested models is answered by these two numbers and nothing else. */
  searchThreshold: number;
}

/** The rail shows 8 options, then `Show all (N)` — M5 (Amazon)'s "See more". */
export const VISIBLE_CAP = 8;
/** Above 20 options a group earns its own `.input--sm` filter box — M1 (Klarna)'s `Find brand`. */
export const SEARCH_THRESHOLD = 20;

/** The bucket a row with no value for a facet falls into. NEVER a drop — that is AUD-1 F-18
 *  (templates with no product type vanished from the catalog tree while staying in the list)
 *  made impossible by construction. */
export const NONE = "(none)";
export const NONE_LABEL = "Not set";

/**
 * Everything `facetOptions` / `applyFacets` need that is not a template.
 *
 * `hasBakedAssets` is a function rather than a map because WS-P reads it off the row's own
 * `currentVersion` — and `undefined` there means "an older cloud did not say", which must never
 * become `not-published` (see `devicePresence.ts`). Passing it as a resolver keeps that rule in
 * WS-P's file, where it belongs.
 */
export interface FacetCtx {
  phoneModelsById: ReadonlyMap<string, PhoneModelOption>;
  /** The per-device "Remove from this device" hide-list. */
  hiddenIds: ReadonlySet<string>;
  /** Templates with a local record (synced here at some point). */
  syncedIds: ReadonlySet<string>;
  /** templateId → every saved view's mock-up is present in this device's store. */
  bakesLocal: ReadonlyMap<string, boolean>;
  /** Defaults to the row's own `currentVersion.hasBakedAssets`. */
  hasBakedAssets?: (t: ProductTemplate) => boolean | undefined;
  /** D-4 (iii): an admin sees every status; a customer sees Active only. */
  canSeeAllStatuses: boolean;
}

/* ───────────────────────────── the per-row cache ───────────────────────────── */

interface RowFacets {
  productType: string;
  productTypeLabel: string;
  brand: string;
  model: string;
  status: StatusFacet | null;
  library: "shared" | "account";
  device: DevicePresence;
}

/**
 * Each row's six facet values, resolved ONCE and keyed on the template OBJECT.
 *
 * This one cache is the whole of G-40's headroom. `facetOptions` is called on every render of a
 * page whose search box the operator is typing into; without it, `brandOf` / `categoryLabel` /
 * `modelLabel` / `devicePresence` run 2 000 × 6 times per keystroke. Measured at K3-2 over
 * 2 000 rows: see progress.md for the median and the cold/warm pair.
 *
 * A `WeakMap` rather than a `Map`: the key is the row object, so an entry dies with the row and
 * a long-lived module-level cache cannot leak a tenant's catalog.
 *
 * The context is part of the key's MEANING — `device` depends on `hiddenIds` / `syncedIds` /
 * `bakesLocal`, all of which change — so the cache is per (row, ctx-generation) and the
 * generation is a cheap monotonic stamp taken from the ctx object identity.
 */
const ROW_CACHE = new WeakMap<object, { ctx: FacetCtx; facets: RowFacets }>();

function facetsOf(t: ProductTemplate, ctx: FacetCtx): RowFacets {
  const hit = ROW_CACHE.get(t as unknown as object);
  if (hit && hit.ctx === ctx) return hit.facets;

  const rawType = (t.display?.category ?? "").trim();
  const rawModel = (t.display?.model ?? "").trim();
  const brand = brandOf(t, ctx.phoneModelsById);
  const modelDisplay = rawModel ? modelLabel({ model: rawModel }) : "";
  const head = headStatusOf(t);
  const facets: RowFacets = {
    productType: rawType || NONE,
    productTypeLabel: rawType ? categoryLabel(rawType) : NONE_LABEL,
    // `brandOf` returns an em dash when neither the server nor the catalog can answer — that is
    // an absence, so it joins the `Not set` bucket rather than becoming a brand called "—".
    brand: brand && brand !== "—" ? brand : NONE,
    model: modelDisplay && modelDisplay !== "—" ? modelDisplay : NONE,
    status: head ? (STATUS_FACETS.find((f) => f.key !== "all" && facetMatches(f.key, head))?.key ?? null) : null,
    library: t.origin === "platform" || t.isLibrary ? "shared" : "account",
    device: devicePresence({
      hidden: ctx.hiddenIds.has(t.templateId),
      synced: ctx.syncedIds.has(t.templateId),
      hasBakedAssets: ctx.hasBakedAssets
        ? ctx.hasBakedAssets(t)
        : (t.currentVersion as { hasBakedAssets?: boolean } | undefined)?.hasBakedAssets,
      bakesLocal: ctx.bakesLocal.get(t.templateId) ?? false,
    }),
  };
  ROW_CACHE.set(t as unknown as object, { ctx, facets });
  return facets;
}

/** The label a facet VALUE renders as, per group. */
function optionLabel(key: FacetKey, value: string, f: RowFacets): string {
  if (value === NONE) return NONE_LABEL;
  switch (key) {
    case "productType": return f.productTypeLabel;
    case "status": return STATUS_FACETS.find((s) => s.key === value)?.label ?? statusLabel(value as never);
    case "library": return value === "shared" ? "Shared library" : "This account";
    // WS-P's own sentence for the state, never a third vocabulary invented here. `ready` has
    // no badge (it is the good state), so it gets the one word the block prints beside it.
    case "device": return value === "ready" ? "On this device" : (presenceBadge(value as DevicePresence)?.label ?? value);
    default: return value;
  }
}

/** Which groups this viewer gets at all. D-4 (iii): a group over one value does nothing. */
function groupsFor(ctx: FacetCtx): readonly FacetKey[] {
  return ctx.canSeeAllStatuses ? FACET_ORDER : FACET_ORDER.filter((k) => k !== "status");
}

/** Does `t` satisfy the selection for ONE group? An empty group selects everything. */
function matchesGroup(f: RowFacets, key: FacetKey, values: readonly string[]): boolean {
  if (values.length === 0) return true;
  return values.includes(f[key] === null ? NONE : String(f[key]));
}

/* ───────────────────────────── the public surface ───────────────────────────── */

/**
 * Narrow `rows` by the selection. **AND across groups, OR within a group.**
 *
 * `Brand: [Apple, Samsung]` + `Status: [Active]` is *(Apple **or** Samsung) **and** Active*.
 * That is the only multi-select semantics a user expects, and the one M1/M2/M5 all use.
 *
 * An empty selection returns the INPUT ARRAY REFERENCE — the cheapest possible no-op, and a
 * property the tests assert, so `visible === shown` when nothing is filtered and every memo
 * downstream keeps its identity.
 */
export function applyFacets(
  rows: readonly ProductTemplate[],
  sel: FacetSelection,
  ctx: FacetCtx,
): ProductTemplate[] {
  const keys = groupsFor(ctx).filter((k) => sel[k].length > 0);
  if (keys.length === 0) return rows as ProductTemplate[];
  return rows.filter((t) => {
    const f = facetsOf(t, ctx);
    return keys.every((k) => matchesGroup(f, k, sel[k]));
  });
}

/**
 * Every group, with counts, for the rows currently loaded.
 *
 * **The count rule, and it is the easiest thing in this wave to get subtly wrong (M2, M5):** a
 * group's counts are computed with **its own selection ignored** and **every other group's
 * applied**. Selecting *Apple* must not make *Samsung* read 0 — that makes the rail unusable
 * the moment it is used. Selecting *Active* **must** change every brand's count.
 *
 * **A zero count renders, disabled, and is never removed (M7).** An operator learns the shape of
 * the catalog from the rail; an option that appears and disappears as you click is the single
 * most disorienting thing a filter rail can do. A zero option the user has SELECTED is not
 * disabled — they have to be able to undo it.
 *
 * One pass over `rows` builds every group's tallies.
 */
export function facetOptions(
  rows: readonly ProductTemplate[],
  sel: FacetSelection,
  ctx: FacetCtx,
): FacetGroup[] {
  const keys = groupsFor(ctx);
  /** key → value → { count, label } */
  const tally = new Map<FacetKey, Map<string, { count: number; label: string }>>();
  for (const k of keys) tally.set(k, new Map());

  for (const t of rows) {
    const f = facetsOf(t, ctx);
    for (const k of keys) {
      // Every OTHER group applies; this one does not (that is the whole rule).
      const passesOthers = keys.every((other) => other === k || matchesGroup(f, other, sel[other]));
      const raw = f[k];
      const value = raw === null ? NONE : String(raw);
      const bucket = tally.get(k)!;
      let entry = bucket.get(value);
      if (!entry) {
        entry = { count: 0, label: optionLabel(k, value, f) };
        bucket.set(value, entry);
      }
      if (passesOthers) entry.count += 1;
    }
  }

  const groups: FacetGroup[] = [];
  for (const k of keys) {
    const selected = new Set(sel[k]);
    const bucket = tally.get(k)!;
    /* A CLOSED vocabulary lists every one of its values, always, at whatever count it has.
     *
     * `status` is D-4's four chips and `library` is a two-way split: both are enumerations the
     * product defines, not shapes discovered from the data, so an operator reading the rail
     * should see the whole vocabulary and learn that this account has no Drafts — rather than
     * watch a chip appear the first time one exists. This is M7's "a count with no option is
     * still information", applied where it is unambiguously true.
     *
     * `device` is deliberately NOT seeded. Its five values are WS-P's DIAGNOSTIC states, and
     * permanently showing `Mock-up not published (0)` beside `Mock-up not downloaded (0)` is
     * exactly the badge dilution WS-P's own review-1 m-5 removed from the row. `brand`, `model`
     * and `productType` are open vocabularies — seeding them is not even possible.
     */
    const CLOSED: Partial<Record<FacetKey, readonly string[]>> = {
      status: STATUS_FACETS.filter((f) => f.key !== "all").map((f) => f.key),
      library: ["account", "shared"],
    };
    for (const v of CLOSED[k] ?? [])
      if (!bucket.has(v)) bucket.set(v, { count: 0, label: optionLabel(k, v, {} as RowFacets) });
    /* A value the operator has selected but which no loaded row carries any more (a stored
       selection against a catalog that moved) still gets an option, at zero — otherwise the
       chip above the results names a filter the rail cannot show or unset. */
    for (const v of selected)
      if (!bucket.has(v))
        bucket.set(v, { count: 0, label: optionLabel(k, v, { productTypeLabel: NONE_LABEL } as RowFacets) });

    const options: FacetOption[] = [...bucket.entries()].map(([value, { count, label }]) => ({
      value,
      label,
      count,
      selected: selected.has(value),
      disabled: count === 0 && !selected.has(value),
    }));
    /* Count descending, then label ascending. The tie-break on the label is what makes the
       order STABLE across renders — without it two equal counts swap places whenever the
       insertion order changes, and the rail visibly shuffles under the pointer. `Not set` is
       pinned last regardless: it is an absence, not a value competing for attention. */
    options.sort((a, b) => {
      if (a.value === NONE) return 1;
      if (b.value === NONE) return -1;
      return b.count - a.count || a.label.localeCompare(b.label);
    });
    /* review-1 m-2 — a group with ONE option STAYS, and here is why.
     *
     * The finding is fair on its face: `Product type → "Phone case (TPU) 6"` is a checkbox that
     * cannot narrow, and F-23 says a control that would do nothing is not rendered. It was
     * implemented (`if (options.length < 2 && !selected.size) continue`) and then reverted,
     * because a group's option SET is drawn from `rows` — which the search box, the status
     * chips and every refetch change. Dropping the group makes it VANISH AND REAPPEAR as the
     * operator types: `Brand` disappears the moment a search leaves one brand standing, and
     * comes back on the next keystroke. That is M7's own disorientation ("an option that
     * appears and disappears as you click is the single most disorienting thing a filter rail
     * can do") one level up, and it is worse at group granularity than at option granularity.
     *
     * The other half: a group is a READING of the catalog as well as a control. "Product type:
     * Phone case (TPU) 6" tells an operator this catalog has exactly one product type — the
     * same information M7 keeps a zero option on screen for. F-23 is about a control that does
     * nothing; this one still says something.
     *
     * `groupsFor`'s customer-side drop of `status` is NOT the same case: that is a CAPABILITY
     * (a customer never sees a non-Active template, whatever the catalog holds), so it is
     * stable for the life of the session.
     */
    groups.push({
      key: k,
      legend: FACET_LEGEND[k],
      options,
      visibleCap: VISIBLE_CAP,
      searchThreshold: SEARCH_THRESHOLD,
    });
  }
  return groups;
}

export interface ActiveChip {
  key: FacetKey;
  value: string;
  label: string;
  /** The accessible name of the chip's ×. A bare × tells a screen-reader user nothing. */
  removeLabel: string;
}

/**
 * The chip summary above the results: one chip per selected value, **in group order** (M1).
 *
 * Driven off `groups` rather than off the selection alone, so a chip cannot name a group the
 * viewer does not have — a stale stored `status` selection on a customer surface produces no
 * chip, because there is no control that could remove it.
 */
export function activeChips(sel: FacetSelection, groups: FacetGroup[]): ActiveChip[] {
  const out: ActiveChip[] = [];
  for (const g of groups) {
    const chosen = sel[g.key];
    if (!chosen.length) continue;
    // In the group's own option order, so the chips read in the same order as the rail.
    for (const opt of g.options) {
      if (!chosen.includes(opt.value)) continue;
      out.push({
        key: g.key,
        value: opt.value,
        label: opt.label,
        removeLabel: `Remove filter: ${opt.label}`,
      });
    }
  }
  return out;
}

/**
 * What the rail sends to the SERVER — and why, today, that is **nothing**.
 *
 * The cloud *can* express two of the six groups (`brand`, `product_category`), and K3 shipped
 * exactly that: a group with one selected value went out as a filter, a multi-select stayed
 * client-side. review-1 B-1 measured what it cost, and the cost is the rail's whole purpose:
 *
 *   · `GET /api/templates?brand=Apple` replaces `templates` with the Apple rows;
 *   · `facetOptions(visible, …)` counts over *that* — the set the rail itself removed;
 *   · so the Brand group's only remaining option is `Apple`. Not disabled at zero (M7's
 *     contract) — **gone**. "Apple **or** Samsung", the owner's literal ask (O-3), becomes
 *     unreachable without first un-checking Apple;
 *   · and `M` in "Showing N of M" shrinks 6 → 3, which is the one job that sentence has.
 *
 * A group's counts are computed with every OTHER group applied and its own ignored. That rule
 * is meaningless if the group has already narrowed the rows it is counting. So the rule wins:
 * **every facet is client-side, and the load the rail counts over is never narrowed by the
 * rail.** `applyFacets` already filters brand and product type correctly, so this costs one
 * round trip per load and nothing else — the server filter was only ever an optimisation.
 *
 * The seam stays here, and it stays empty by construction, because the fix is a *server*
 * feature: `GET /api/templates/facets` (counts over the whole catalog — ticketed to WS-C) is
 * what lets the load be narrowed while the vocabulary stays whole. The day it exists, this
 * function fills `server` again and `facetOptions` takes its counts from the endpoint.
 *
 * `clientOnly` therefore lists every group carrying a selection — which is the honest answer
 * to "who narrowed these rows": all of them, here.
 */
export function splitFilters(sel: FacetSelection): { server: Filters; clientOnly: FacetKey[] } {
  return {
    server: { ...EMPTY_FILTERS },
    clientOnly: FACET_ORDER.filter((k) => sel[k].length > 0),
  };
}

/** Toggle one value in one group. Never mutates. */
export function toggleFacet(sel: FacetSelection, key: FacetKey, value: string): FacetSelection {
  const cur = sel[key];
  const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
  return { ...sel, [key]: next };
}

/** Clear one group, or all of them. Never mutates. */
export function clearFacets(sel: FacetSelection, key?: FacetKey): FacetSelection {
  if (!key) return EMPTY_SELECTION;
  return { ...sel, [key]: [] };
}

/** How many VALUES are selected — the number the `Filters (N)` badge and `Clear all` read. */
export function activeFacetCount(sel: FacetSelection): number {
  return FACET_ORDER.reduce((n, k) => n + sel[k].length, 0);
}

/**
 * Which single group, if removed, would bring back the most rows — and how many.
 *
 * This is the number behind K3-11's "Clearing Brand would show 14" sentence: it turns a dead
 * end into a next step, and it costs nothing because it is `applyFacets` with one group
 * cleared. `null` when fewer than two groups are active, because with one active group
 * "clearing it would show everything" is not information.
 */
export function biggestReduction(
  rows: readonly ProductTemplate[],
  sel: FacetSelection,
  ctx: FacetCtx,
): { key: FacetKey; legend: string; wouldShow: number } | null {
  const active = groupsFor(ctx).filter((k) => sel[k].length > 0);
  if (active.length < 2) return null;
  let best: { key: FacetKey; legend: string; wouldShow: number } | null = null;
  for (const k of active) {
    const wouldShow = applyFacets(rows, clearFacets(sel, k), ctx).length;
    if (!best || wouldShow > best.wouldShow) best = { key: k, legend: FACET_LEGEND[k], wouldShow };
  }
  return best;
}
